import type { ManageCollectionRequest } from '@kimuno/api-sdk/lib/types/requests/Common';
import type { ItemVariation } from '@kimuno/api-sdk/lib/types/responses/Collection';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import {
	manageMindItemInMultipleCollections,
	manageProductInMultipleCollections,
} from '../actions/collections.actions';
import type { ModalCollectionItem } from '../constants';
import type { StoreType } from '../reducers';
import {
	type CollectionWithContents,
	selectCollectionsWithContents,
} from '../reducers/collections.selectors';

type SetItemFunc = (itemId: string, itemType: ItemVariation) => void;
type ToggleCollectionFunc = (collectionId: string) => void;
type SaveFunc = () => Promise<void>;
type SetSelectedCollectionsFunc = (data: ModalCollectionItem[]) => void;

const noop = () => undefined;

export type CollectionWithContainsItemFlag = CollectionWithContents & {
	containsItem: boolean;
};

interface Context {
	collections: CollectionWithContainsItemFlag[];
	save: SaveFunc;
	toggleCollection: ToggleCollectionFunc;
	setItem: SetItemFunc;
	selectedCollections: ModalCollectionItem[];
	setSelectedCollections: SetSelectedCollectionsFunc;
	itemBelongsToAtLeastOneCollection: boolean;
}

const defaultContext: Context = {
	collections: [],
	save: () => Promise.resolve(),
	toggleCollection: noop,
	setItem: noop,
	selectedCollections: [],
	setSelectedCollections: noop,
	itemBelongsToAtLeastOneCollection: false,
};

export const ManageItemInCollectionContext = React.createContext(defaultContext);

interface StateProps {
	collections: CollectionWithContents[];
}

interface DispatchProps {
	manageProductInMultipleCollections: (
		itemId: string,
		actionPayload: ManageCollectionRequest['collections']
	) => Promise<any>;
	manageMindItemInMultipleCollections: (
		itemId: string,
		actionPayload: ManageCollectionRequest['collections']
	) => Promise<any>;
}

const ManageItemInCollectionProviderInner: React.FC<StateProps & DispatchProps> = (props) => {
	const [collections, setCollections] = useState<CollectionWithContainsItemFlag[]>([]);
	const [currentItem, setCurrentItem] = useState<{
		id: string;
		type: ItemVariation;
	} | null>(null);
	const [selectedCollections, setSelectedCollections] = useState<ModalCollectionItem[]>([]);
	const [resetKey, setResetKey] = useState(0);
	const [itemBelongsToAtLeastOneCollection, setItemBelongsToAtLeastOneCollection] = useState(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!currentItem) {
			setItemBelongsToAtLeastOneCollection(false);
			return;
		}

		const initialState = props.collections.map((c) => {
			const containsItem =
				c.contents.findIndex((item) => {
					return item.id === currentItem.id && item.type === currentItem.type;
				}) !== -1;

			const collection: CollectionWithContainsItemFlag = {
				...c,
				containsItem,
			};

			return collection;
		});

		const isItemInInitialState = initialState.some((c) => c.containsItem);
		setItemBelongsToAtLeastOneCollection(isItemInInitialState);
		setCollections(initialState);
		setSelectedCollections([]);
	}, [props.collections, currentItem, resetKey]);

	const setItem = (itemId: string, itemType: ItemVariation) => {
		// Increment resetKey to force useEffect to run and reinicialize state
		// This ensures that when the modal is reopened, the state is reset to the initial values
		if (currentItem?.id !== itemId || currentItem?.type !== itemType) setResetKey((prev) => prev + 1);
		setCurrentItem({ id: itemId, type: itemType });
	};

	const save = () => {
		if (!currentItem) {
			return Promise.reject();
		}

		const actionPayload = collections.map((c) => ({
			collectionId: c.id,
			shouldContainItem: c.containsItem,
		}));
		if (currentItem.type === 'mindItem') {
			return props.manageMindItemInMultipleCollections(currentItem.id, actionPayload);
		}

		if (currentItem.type === 'product') {
			return props.manageProductInMultipleCollections(currentItem.id, actionPayload);
		}
		return Promise.reject();
	};

	const toggleCollection = (collectionId: string) => {
		const updatedCollections = collections.map((collection: CollectionWithContainsItemFlag) => {
			if (collection.id !== collectionId) {
				return collection;
			}

			setListOfSelectedCollections(collectionId, collection);

			return {
				...collection,
				containsItem: !collection.containsItem,
			};
		});
		setCollections(updatedCollections);
	};

	const setListOfSelectedCollections = (
		collectionId: string,
		collection: CollectionWithContainsItemFlag
	) => {
		// The logic required for getting the list of selected collections.
		// Used int the amplitude events
		const collectionIndex: number = selectedCollections.findIndex(
			(checkbox: ModalCollectionItem) => checkbox.id === collectionId
		);

		if (collectionIndex !== -1) {
			selectedCollections.splice(collectionIndex, 1);
		} else {
			selectedCollections.push({
				id: collectionId,
				isSelected: !collection.containsItem,
			});
		}

		setSelectedCollections(selectedCollections);
	};

	return (
		<ManageItemInCollectionContext.Provider
			value={{
				setItem,
				save,
				collections,
				toggleCollection,
				selectedCollections,
				setSelectedCollections,
				itemBelongsToAtLeastOneCollection,
			}}
		>
			{props.children}
		</ManageItemInCollectionContext.Provider>
	);
};

const mapState = (state: StoreType): StateProps => {
	return {
		collections: selectCollectionsWithContents(state),
	};
};

const mapDispatch = {
	manageMindItemInMultipleCollections,
	manageProductInMultipleCollections,
};

export const ManageItemInCollectionProvider = connect(
	mapState,
	mapDispatch
)(ManageItemInCollectionProviderInner);
